import { useEffect, useRef, useState } from "react";
import { Download, Square } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { getInstallProgress, getHfRepoSize, stopInstall } from "@/lib/agent";

const gb = (b) => (b / 1e9).toFixed(2);
const fmtTime = (s) => {
  if (!isFinite(s) || s <= 0) return "—";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h} h ${m} min` : `${Math.max(m, 1)} min`;
};

export function InstallProgressBanner() {
  const [p, setP] = useState(null);
  const [total, setTotal] = useState(null);
  const [speed, setSpeed] = useState(0);
  const last = useRef(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const d = await getInstallProgress().catch(() => null);
      if (!alive) return;
      if (!d?.active) { setP(null); last.current = null; return; }
      const now = Date.now();
      if (last.current && d.bytes >= last.current.bytes) {
        const inst = (d.bytes - last.current.bytes) / ((now - last.current.t) / 1000);
        setSpeed((s) => (s ? s * 0.7 + inst * 0.3 : inst));
      }
      last.current = { bytes: d.bytes, t: now };
      setP(d);
      if (d.repo_id) getHfRepoSize(d.repo_id).then((t) => alive && setTotal(t)).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!p) return null;
  const pct = total ? Math.min(100, (p.bytes / total) * 100) : null;
  const eta = total && speed > 0 ? (total - p.bytes) / speed : NaN;

  const stop = async () => {
    if (!window.confirm("¿Pausar la descarga? Lo descargado se conserva y podrás reanudarla.")) return;
    const d = await stopInstall(null, { repo_id: p.repo_id, name: p.name });
    d?.ok ? toast.success(d.message || "Descarga en pausa") : toast.warning(d?.error);
  };

  return (
    <div className="mb-6 rounded-sm border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="install-progress-banner">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-emerald-300">
          <Download className="h-4 w-4 animate-pulse" />
          <span className="font-heading font-semibold">Descargando {p.name || p.repo_id}</span>
          <span className="font-mono text-[11px] text-[#a3a3a3]">{p.repo_id}</span>
        </div>
        <button onClick={stop} data-testid="install-progress-stop" className="inline-flex items-center gap-1.5 rounded-sm border border-red-500/50 bg-red-500/10 px-3 py-1 text-xs text-red-300 hover:bg-red-500/20">
          <Square className="h-3 w-3" />Detener
        </button>
      </div>
      <Progress value={pct ?? 0} className="mt-3 h-2 bg-[#1a1a1a] [&>div]:bg-emerald-400" data-testid="install-progress-bar" />
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[11px] text-[#d4d4d4]" data-testid="install-progress-stats">
        <span>{gb(p.bytes)} GB{total ? ` de ${gb(total)} GB` : ""}{pct != null ? ` · ${pct.toFixed(1)}%` : ""}</span>
        <span>{speed > 0 ? `${(speed / 1e6).toFixed(1)} MB/s` : p.downloading ? "midiendo velocidad…" : "preparando entorno…"}</span>
        <span>Quedan: {fmtTime(eta)}</span>
        {p.attempt > 1 && <span className="text-amber-300">Reintento {p.attempt}/{p.max_attempts} (continúa donde iba)</span>}
      </div>
      {p.path && <p className="mt-1 break-all font-mono text-[10px] text-[#737373]">Destino: {p.path}</p>}
    </div>
  );
}
